import React from 'react';
import {
  HStack,
  Box,
  Spinner,
  Pressable,
  Text,
  Icon,
} from '@gluestack-ui/themed';
import { ClipboardList, CheckCircle } from 'lucide-react-native';
import SearchWithFilters from '@/admin/common/SearchWithFilters';

interface EvaluationFiltersProps {
  onGlobalFilterChange: (value: string) => void;
  columnFilters: Array<{ id: string; value: unknown }>;
  onColumnFiltersChange: (filters: Array<{ id: string; value: unknown }>) => void;
  isLoading?: boolean;
}

type ViewState = 'submitted' | 'graded';

/**
 * Compact filter bar for the Evaluation Table.
 * Features search with autocomplete and a toggle switch for state filtering.
 */
export const EvaluationFilters: React.FC<EvaluationFiltersProps> = ({
  onGlobalFilterChange,
  columnFilters,
  onColumnFiltersChange,
  isLoading = false,
}) => {
  // Extract state filter from column filters
  const stateFilter = columnFilters.find((f) => f.id === 'state');
  const currentState = (stateFilter?.value as ViewState) || 'submitted';

  const handleStateChange = (newState: ViewState) => {
    const newFilters = columnFilters.filter((f) => f.id !== 'state');
    newFilters.push({ id: 'state', value: newState });
    onColumnFiltersChange(newFilters);
  };

  return (
    <HStack
      px="$6"
      py="$3"
      borderBottomWidth={1}
      borderBottomColor="$borderLight"
      alignItems="center"
      space="md"
    >
      {/* Search with autocomplete */}
      <Box flex={1} maxWidth={600}>
        <SearchWithFilters
          testID="evaluation-search-input"
          onSearch={onGlobalFilterChange}
          searchPlaceholder="Search by student, module..."
          isDisabled={isLoading}
          searchableFields={['student', 'module', 'grade']}
          defaultSearchField="student"
        />
      </Box>

      {/* Loading spinner */}
      {isLoading && <Spinner size="small" />}

      {/* State toggle switch */}
      <HStack alignItems="center" space="xs" bg="$bgHover" p="$1" borderRadius="$lg">
        <Pressable
          onPress={() => handleStateChange('submitted')}
          px="$3"
          py="$1.5"
          borderRadius="$md"
          bg={currentState === 'submitted' ? '$bgCard' : 'transparent'}
          softShadow={currentState === 'submitted' ? '1' : undefined}
          disabled={isLoading}
        >
          <HStack alignItems="center" space="xs">
            <Icon
              as={ClipboardList}
              size="xs"
              color={currentState === 'submitted' ? '$textHeading' : '$textSubtle'}
            />
            <Text
              size="sm"
              color={currentState === 'submitted' ? '$textHeading' : '$textSubtle'}
              fontWeight={currentState === 'submitted' ? 'semibold' : 'medium'}
            >
              Waiting for Grading
            </Text>
          </HStack>
        </Pressable>
        <Pressable
          onPress={() => handleStateChange('graded')}
          px="$3"
          py="$1.5"
          borderRadius="$md"
          bg={currentState === 'graded' ? '$bgCard' : 'transparent'}
          softShadow={currentState === 'graded' ? '1' : undefined}
          disabled={isLoading}
        >
          <HStack alignItems="center" space="xs">
            <Icon
              as={CheckCircle}
              size="xs"
              color={currentState === 'graded' ? '$textHeading' : '$textSubtle'}
            />
            <Text
              size="sm"
              color={currentState === 'graded' ? '$textHeading' : '$textSubtle'}
              fontWeight={currentState === 'graded' ? 'semibold' : 'medium'}
            >
              Graded
            </Text>
          </HStack>
        </Pressable>
      </HStack>
    </HStack>
  );
};

export default EvaluationFilters;
